import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { CalendarIcon, DollarSign, CreditCard, Banknote, FileText, Receipt } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PaymentMethod, PAYMENT_METHODS } from '@/types/constants';
import { Family } from '@/types/models';
import { ManualPaymentService, ManualPaymentData } from '@/services/payment/manualPaymentService';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { toast } from 'sonner';
import { parseCurrencyToCents, formatCurrency } from '@/lib/format';

const manualPaymentSchema = z.object({
  family_id: z.string().min(1, 'Please select a family'),
  amount: z.string().min(1, 'Amount is required'),
  payment_method: z.enum(['cash', 'check', 'money_order', 'manual_card']),
  paid_at: z.date(),
  check_number: z.string().optional(),
  manual_notes: z.string().optional(),
  invoice_id: z.string().optional(),
});

type ManualPaymentFormValues = z.infer<typeof manualPaymentSchema>;

interface ManualPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  families: Family[];
  organizationId: string;
  onPaymentCreated: () => void;
  initialFamilyId?: string;
}

interface Invoice {
  id: string;
  total_cents: number;
  due_date: string;
  created_at: string;
}

const PAYMENT_METHOD_ICONS = {
  cash: Banknote,
  check: FileText,
  money_order: Receipt,
  manual_card: CreditCard,
};

const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  check: 'Check',
  money_order: 'Money Order',
  manual_card: 'Manual Card',
};

const ManualPaymentDialog: React.FC<ManualPaymentDialogProps> = ({
  open,
  onOpenChange,
  families,
  organizationId,
  onPaymentCreated,
  initialFamilyId,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const form = useForm<ManualPaymentFormValues>({
    resolver: zodResolver(manualPaymentSchema),
    defaultValues: {
      family_id: initialFamilyId || '',
      amount: '',
      payment_method: 'cash',
      paid_at: new Date(),
      check_number: '',
      manual_notes: '',
      invoice_id: 'none',
    },
  });

  const selectedFamilyId = form.watch('family_id');
  const selectedPaymentMethod = form.watch('payment_method');

  const loadUnpaidInvoices = useCallback(async (familyId: string) => {
    setLoadingInvoices(true);
    try {
      const invoices = await ManualPaymentService.getUnpaidInvoicesForFamily(familyId, organizationId);
      setAvailableInvoices(invoices as Invoice[]);
    } catch (error) {
      const context: ErrorContext = {
        operation: 'loadUnpaidInvoices',
        additionalInfo: { familyId, organizationId },
      };
      handleError(error, 'Failed to load unpaid invoices', ErrorSeverity.WARNING, context);
    } finally {
      setLoadingInvoices(false);
    }
  }, [organizationId]);

  // Load unpaid invoices when family changes
  useEffect(() => {
    if (selectedFamilyId && organizationId) {
      loadUnpaidInvoices(selectedFamilyId);
    } else {
      setAvailableInvoices([]);
    }
  }, [selectedFamilyId, organizationId, loadUnpaidInvoices]);

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return;
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return;
    }
    
    form.setValue('amount', numericValue);
  };

  const handleSubmit = async (data: ManualPaymentFormValues) => {
    setIsSubmitting(true);
    
    try {
      const amountCents = parseCurrencyToCents(data.amount);
      
      if (amountCents <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }

      const paymentData: ManualPaymentData = {
        family_id: data.family_id,
        organization_id: organizationId,
        amount_cents: amountCents,
        payment_method: data.payment_method as Exclude<PaymentMethod, 'stripe'>,
        paid_at: data.paid_at.toISOString(),
        check_number: data.check_number?.trim() || undefined,
        manual_notes: data.manual_notes?.trim() || undefined,
        invoice_id: data.invoice_id === 'none' ? undefined : data.invoice_id || undefined,
      };

      const result = await ManualPaymentService.createManualPayment(paymentData);

      if (result) {
        // Send receipt email in the background (don't block on it)
        ManualPaymentService.sendPaymentReceiptEmail(result).then((emailSent) => {
          if (emailSent) {
            console.log('Receipt email sent successfully');
          } else {
            console.warn('Failed to send receipt email, but payment was recorded');
          }
        });

        toast.success(`Payment recorded successfully! Receipt #${result.receipt_number}`, {
          description: `${formatCurrency(amountCents / 100)} payment via ${PAYMENT_METHOD_LABELS[data.payment_method]}`,
        });
        onPaymentCreated();
        onOpenChange(false);
        form.reset();
      } else {
        toast.error('Failed to record payment. Please try again.');
      }
    } catch (error) {
      const context: ErrorContext = {
        operation: 'createManualPayment',
        additionalInfo: { organizationId },
      };
      handleError(error, 'Failed to record manual payment', ErrorSeverity.ERROR, context);
      toast.error('An error occurred while recording the payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCalendarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" />
            Record Manual Payment
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Family Selection */}
            <FormField
              control={form.control}
              name="family_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Family *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a family" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {families.map((family) => (
                        <SelectItem key={family.id} value={family.id}>
                          {family.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment Method */}
            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PAYMENT_METHODS.filter(method => method !== 'stripe').map((method) => {
                        const Icon = PAYMENT_METHOD_ICONS[method as keyof typeof PAYMENT_METHOD_ICONS];
                        return (
                          <SelectItem key={method} value={method}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {PAYMENT_METHOD_LABELS[method as keyof typeof PAYMENT_METHOD_LABELS]}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount */}
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        <DollarSign className="h-4 w-4" />
                      </span>
                      <Input
                        {...field}
                        placeholder="0.00"
                        onChange={handleCurrencyChange}
                        inputMode="decimal"
                        className="pl-9"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payment Date */}
            <FormField
              control={form.control}
              name="paid_at"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Payment Date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick payment date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto p-0 z-50" 
                      align="start"
                      onClick={handleCalendarClick}
                    >
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Check Number (conditional) */}
            {selectedPaymentMethod === 'check' && (
              <FormField
                control={form.control}
                name="check_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Check Number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter check number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Invoice Selection (optional) */}
            {availableInvoices.length > 0 && (
              <FormField
                control={form.control}
                name="invoice_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apply to Invoice (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select invoice to pay" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No specific invoice</SelectItem>
                        {availableInvoices.map((invoice) => (
                          <SelectItem key={invoice.id} value={invoice.id}>
                            {formatCurrency(invoice.total_cents / 100)} - Due {format(new Date(invoice.due_date), 'MMM d, yyyy')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Notes */}
            <FormField
              control={form.control}
              name="manual_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Additional notes about this payment..."
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? 'Recording...' : 'Record Payment'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualPaymentDialog;